import {get, has} from 'lodash';

import type {JSONNode} from '@packages/shared/types/JSONNode';
import {
  type EvaluatedNodeData,
  getBooleanOrElse,
  getInPojo,
  getNumber,
  getString,
  getStringOrElse,
} from '@packages/utilities/data-utils';
import {RENDER_MODES} from '@packages/systems/core/constants/Lottie';

export type LottieData = {
  type: string | null;
  src: string;
  id: string | null;
  origFileName: string;
  totalDuration: number | null;
  loop: boolean;
  playInReverse: boolean;
  duration: number | null;
  useBuiltInDuration: boolean;
  renderer: 'svg' | 'canvas';
  loading: 'eager' | 'lazy';
};

const originFileNamePath = ['val', 'path', 'origFileName'];
const totalDurationPath = ['val', 'path', 'totalDuration'];
const srcPath = ['val', 'path', 'src'];
const idPath = ['val', 'path', 'id'];
const loopPath = ['val', 'loop'];
const playInReversePath = ['val', 'playInReverse'];
const durationPath = ['val', 'duration'];
const renderModePath = ['val', 'renderMode'];
const loadingPath = ['val', 'loading'];

/**
 * Extracts the Lottie animation data from the atom's evaluated `data`.
 */
export const getLottieData = (data: EvaluatedNodeData): LottieData | null => {
  const animation = getInPojo(data, ['animation']);

  if (animation == null) return null;

  const type = getString(getInPojo(animation, ['tag'])) ?? null;
  const src = getString(getInPojo(animation, srcPath)) ?? null;
  const id = getString(getInPojo(animation, idPath)) ?? null;
  const origFileName =
    getString(getInPojo(animation, originFileNamePath)) ?? null;

  /**
   * Because of a quirk in old Lottie animations, this could be either a number
   * or null.
   */
  const rawTotalDuration = getInPojo(animation, totalDurationPath) as
    | number
    | null;
  const totalDuration = rawTotalDuration === undefined ? 0 : rawTotalDuration;

  if (src == null || origFileName == null) return null;

  const loop = getBooleanOrElse(getInPojo(animation, loopPath), false);
  const playInReverse = getBooleanOrElse(
    getInPojo(animation, playInReversePath),
    false
  );
  const duration = getNumber(getInPojo(animation, durationPath)) ?? null;
  const renderer = (getString(getInPojo(animation, renderModePath)) ??
    RENDER_MODES.SVG) as 'svg' | 'canvas';
  const loading = getStringOrElse(
    getInPojo(animation, loadingPath),
    'eager'
  ) as 'eager' | 'lazy';

  const useBuiltInDuration = duration == null;

  return {
    type,

    /**
     * @deprecated Give preference to recover the asset URL using `id` instead.
     */
    src,
    id,
    origFileName,
    loop,
    playInReverse,
    duration,
    useBuiltInDuration,
    totalDuration,
    renderer,
    loading,
  };
};

export const migrationJsonNodeAnimationToUnionType = (jsonNode: JSONNode) => {
  const {data = {}, type} = jsonNode;

  if (type !== 'Animation') {
    return jsonNode;
  }

  const isNewType = has(data, ['animation', 'tag']);

  if (isNewType) {
    return jsonNode;
  }

  const placeholderSrc = get(data, ['animation', 'placeholderSrc']);
  const src = get(data, ['animation', 'config', 'path']);
  const origFileName = get(data, ['animation', 'config', 'origFileName']);
  const totalDuration = get(data, ['animation', 'config', 'totalDuration']);
  const loop = get(data, ['animation', 'loop']);
  const useBuiltInDuration = get(data, ['animation', 'useBuiltInDuration']);
  const playInReverse = get(data, ['animation', 'playInReverse']);
  const duration = get(data, ['animation', 'duration']);
  const renderMode = get(data, ['animation', 'renderer']);

  if (placeholderSrc) {
    return {
      ...jsonNode,
      data: {
        ...data,
        animation: {
          tag: 'lottie',
          val: {placeholderSrc},
        },
      },
    };
  }

  const val: {
    path: {
      src: string;
      origFileName: string;
      totalDuration: number;
    };
    loop?: boolean;
    duration?: number;
    playInReverse?: boolean;
    renderMode?: 'svg' | 'canvas';
  } = {
    path: {
      src,
      origFileName,
      totalDuration,
    },
    renderMode: renderMode || 'svg',
    loop: Boolean(loop),
    playInReverse: Boolean(playInReverse),
  };

  if (duration != null && !useBuiltInDuration) {
    val.duration = duration;
  }

  return {
    ...jsonNode,
    data: {
      ...data,
      animation: {
        tag: 'lottie',
        val,
      },
    },
  };
};

export const LOTTIE_PLACEHOLDER = {
  assetName: 'wf-placeholder.json',
  getPlaceholderData: (url: string) => ({
    id: 'plugins/Animation/assets/wf-placeholder.json',
    src: url,
    origFileName: 'wf-placeholder',
    totalDuration: 4.08,
  }),
} as const;
